import { useId, useMemo, useState } from 'react';
import { DialogContent } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import { getApiErrorMessage } from '@api/getApiError';
import { usePutConsignmentsMutation } from '@api/Trinium/Endpoints/Consignment/slice';
import { useModalProvider } from '@components/Modals/ModalContext';
import FormikModalHeader from '@components/Modals/FormikModalHeader';
import FormikModal from '@components/Modals/FormikModal';
import { isFormikErrorsEmpty } from '@hooks/useFormikValues';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import FormikForm from './Form';
import { MultiContainerUpdateFormik, MultiContainerUpdateProps, MultiContainerUpdateFormikValidationSchema } from './types';

import styles from './MultiContainerUpdate.module.css';

export default function MultiContainerUpdate({ legs }: MultiContainerUpdateProps) {
	const { number: terminalNumber } = useCurrentTerminal();
	const formId = useId();
	const [isOpen, setIsOpen] = useState(true);
	const modalContext = useModalProvider();
	const { queueModalAnnouncement } = modalContext;
	const [putConsignments] = usePutConsignmentsMutation();

	const selectedConsignmentIds = useMemo(() => {
		return legs.map((leg) => leg.csnId);
	}, [legs]);

	const initialValues: MultiContainerUpdateFormik | undefined = useMemo(() => {
		// sets customerRef initial value only if all values are the same
		const selectedCustRefNos = legs.map((leg) => leg.csnCustRefNo);
		const [first, ...remaining] = selectedCustRefNos;
		const isSameCustRefNo = first && remaining.every((val) => val === first);

		return {
			customerRef: isSameCustRefNo ? first : ''
		};
	}, [legs]);

	const onFormSubmit = async (values: MultiContainerUpdateFormik, formikHelpers: FormikHelpers<MultiContainerUpdateFormik>) => {
		const validationErrors = await formikHelpers.validateForm(values);

		if (isFormikErrorsEmpty(validationErrors)) {
			try {
				await putConsignments({
					terminalNumber,
					actionType: {
						action: 'updateCustRefNo',
						data: {
							csnIds: selectedConsignmentIds,
							custRefNo: values.customerRef ?? ''
						}
					}
				}).unwrap();
				setIsOpen(false);
			} catch (error: unknown) {
				const errorMessage = getApiErrorMessage(error);
				queueModalAnnouncement({
					severity: 'error',
					message: errorMessage
				});
			}
		}
	};

	return (
		<FormikModal
			isOpen={isOpen}
			onClose={() => {
				setIsOpen(false);
			}}
			paperClass={styles['multi-container-update']}
		>
			<Formik
				initialValues={initialValues}
				validationSchema={MultiContainerUpdateFormikValidationSchema}
				onSubmit={onFormSubmit}
			>
				{(formikProps) => (
					<>
						<FormikModalHeader
							title='Update Customer Ref'
							formName={formId}
							onClose={() => {
								setIsOpen(false);
							}}
						/>

						<DialogContent>
							<FormikForm
								id={formId}
								{...formikProps}
							/>
						</DialogContent>
					</>
				)}
			</Formik>
		</FormikModal>
	);
}
